import React, { useCallback, useMemo, useState } from 'react';
import { FaArrowDown, FaArrowLeft, FaArrowRight, FaArrowUp, FaBullseye, FaCheck, FaCheckCircle, FaChevronDown, FaPen, FaPlus, FaTimes, FaTrash } from 'react-icons/fa';
import { getMobileBuilderModuleGroups } from '../../services/giftBuilderConfig';
import { getModuleMeta } from '../../services/moduleCatalog';
import { getCompletedModuleCount, isModuleComplete } from '../../services/moduleCompletion';
import LayeredRevealSettingsCard from './LayeredRevealSettingsCard';

const ChapterListStep = ({
  pageData,
  theme,
  focusedModuleId,
  onFocusModule,
  focusedModuleIndex = -1,
  focusedModuleCount = 0,
  onFocusPrevious,
  onFocusNext,
  onDoneFocus,
  onUpdateModuleConfig,
  onAddModule,
  onRemoveModule,
  onMoveModule,
  forceOpen = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showAddPanel, setShowAddPanel] = useState(false);
  const modules = useMemo(() => (
    Array.isArray(pageData?.modules) ? pageData.modules : []
  ), [pageData?.modules]);
  const focusedModule = modules.find((module) => (module.id || module._localId) === focusedModuleId);
  const focusedMeta = focusedModule ? getModuleMeta(focusedModule.type) : null;
  const completeCount = getCompletedModuleCount(modules);
  const allComplete = modules.length > 0 && completeCount === modules.length;
  const showChapterList = forceOpen || isOpen || Boolean(focusedModuleId);
  const moduleGroups = getMobileBuilderModuleGroups(pageData);
  const existingTypes = useMemo(() => new Set(modules.map((module) => module.type)), [modules]);

  const handleModuleSelect = useCallback((moduleId) => {
    onFocusModule?.(moduleId);
    const target = document.getElementById(`gift-module-${moduleId}`);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [onFocusModule]);

  const handleAddModule = useCallback((type) => {
    const addedId = onAddModule?.(type);
    if (addedId) setShowAddPanel(false);
  }, [onAddModule]);

  const handleRemoveModule = useCallback((event, moduleId, label) => {
    event.stopPropagation();
    const confirmed = window.confirm(`Delete ${label}? This removes this chapter from the gift draft.`);
    if (!confirmed) return;
    onRemoveModule?.(moduleId);
  }, [onRemoveModule]);

  const handleMoveModule = useCallback((event, moduleId, direction) => {
    event.stopPropagation();
    onMoveModule?.(moduleId, direction);
  }, [onMoveModule]);

  const renderAddOptions = (title, list = []) => {
    if (!list.length) return null;
    return (
      <section className="mt-4">
        <h4 className="mb-2 text-[8px] font-black uppercase tracking-[0.24em] opacity-45">{title}</h4>
        <div className="lumirae-editor-add-grid">
          {list.map((module) => (
            <button
              key={module.id}
              type="button"
              className="lumirae-editor-add-option"
              onClick={() => handleAddModule(module.id)}
            >
              <span className="lumirae-editor-add-option-icon">{module.icon}</span>
                <span className="min-w-0">
                <span className="block text-[9px] font-black uppercase leading-snug tracking-widest">
                  {module.label}
                </span>
                <span className="mt-1 block text-[9px] font-medium leading-snug opacity-55">
                  {existingTypes.has(module.id) ? 'Already added. Add another only if the limit allows.' : module.shortLabel || module.tier || 'Module'}
                </span>
              </span>
            </button>
          ))}
        </div>
      </section>
    );
  };

  return (
    <section id="gift-builder-chapters" className="scroll-mt-6 lumirae-editor-panel p-5">
      <button
        type="button"
        onClick={() => setIsOpen((value) => !value)}
        className="flex w-full flex-wrap items-center justify-between gap-3 text-left"
      >
        <div>
          <div className="text-[10px] font-black uppercase tracking-[0.28em]" style={{ color: theme.accent }}>
            Step 3 / Modules
          </div>
          <p className="mt-2 text-xs font-medium leading-relaxed opacity-60">
            Tap a chapter to focus the editor on that part of the gift.
          </p>
        </div>
        <span className="flex items-center gap-2">
          <span
            className="inline-flex items-center gap-2 rounded-full bg-white/70 px-3 py-1 text-[8px] font-black uppercase tracking-widest"
            style={{ color: allComplete ? 'var(--lumirae-state-success)' : 'var(--lumirae-text-secondary)' }}
          >
            {allComplete && <FaCheckCircle />}
            {completeCount}/{modules.length} ready
          </span>
          <FaChevronDown className={`transition-transform ${showChapterList ? 'rotate-180' : ''}`} style={{ color: theme.accent }} />
        </span>
      </button>

      {showChapterList && (
      <>
      <div className="mt-4 rounded-[1.5rem] border bg-white/55 p-3" style={{ borderColor: 'var(--lumirae-border-default)' }}>
        <div className="flex items-center gap-3">
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl"
            style={{ backgroundColor: 'var(--lumirae-accent-soft)', color: theme.accent }}
          >
            <FaBullseye />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[9px] font-black uppercase tracking-[0.24em]" style={{ color: theme.accent }}>
              {focusedMeta ? `Chapter ${focusedModuleIndex + 1} of ${focusedModuleCount}` : 'Focus Edit'}
            </div>
            <p className="mt-1 text-xs font-medium leading-relaxed opacity-60">
              {focusedMeta ? `Editing ${focusedMeta.label}. Save stays here; Done returns to the chapter list.` : 'Tap a chapter below to edit one module at a time.'}
            </p>
          </div>
          {focusedModuleId && (
            <button
              type="button"
              onClick={onDoneFocus || (() => onFocusModule?.(''))}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-[10px] shadow-sm"
              aria-label="Done editing chapter"
              style={{ color: theme.secondaryText }}
            >
              <FaCheck />
            </button>
          )}
        </div>
        {focusedModuleId && (
          <div className="mt-3 grid grid-cols-3 gap-2">
            <button
              type="button"
              onClick={onFocusPrevious}
              disabled={focusedModuleIndex <= 0}
              className="lumirae-ds-button lumirae-ds-button-secondary flex items-center justify-center gap-2 px-3 py-3 disabled:opacity-35"
            >
              <FaArrowLeft /> Prev
            </button>
            <button
              type="button"
              onClick={onDoneFocus || (() => onFocusModule?.(''))}
              className="lumirae-ds-button lumirae-ds-button-primary flex items-center justify-center gap-2 px-3 py-3"
            >
              <FaCheck /> Done
            </button>
            <button
              type="button"
              onClick={onFocusNext}
              disabled={focusedModuleIndex < 0 || focusedModuleIndex >= focusedModuleCount - 1}
              className="lumirae-ds-button lumirae-ds-button-secondary flex items-center justify-center gap-2 px-3 py-3 disabled:opacity-35"
            >
              Next <FaArrowRight />
            </button>
          </div>
        )}
        {focusedModuleId && focusedModule && focusedMeta && (
          <div className="mt-4">
            <LayeredRevealSettingsCard
              module={focusedModule}
              moduleName={focusedMeta.label}
              moduleIndex={Math.max(0, focusedModuleIndex)}
              theme={theme}
              isComplete={isModuleComplete(focusedModule)}
              onUpdateModuleConfig={onUpdateModuleConfig}
            />
          </div>
        )}
      </div>

      {!focusedModuleId && modules.length > 0 ? (
        <div className="mt-4 grid grid-cols-1 gap-2">
          <div className="rounded-[1.5rem] border bg-white/55 p-3" style={{ borderColor: 'var(--lumirae-border-default)' }}>
            <button
              type="button"
              className="flex w-full items-center justify-between gap-3 text-left"
              onClick={() => setShowAddPanel((value) => !value)}
            >
              <span>
                <span className="block text-[9px] font-black uppercase tracking-[0.24em]" style={{ color: theme.accent }}>
                  Add Module
                </span>
                <span className="mt-1 block text-xs font-medium opacity-60">
                  Add a new chapter to this Layer-by-layer gift.
                </span>
              </span>
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white shadow-sm" style={{ color: theme.accent }}>
                {showAddPanel ? <FaTimes /> : <FaPlus />}
              </span>
            </button>
            {showAddPanel && (
              <div className="pt-1">
                {renderAddOptions('Recommended', moduleGroups.recommended)}
                {renderAddOptions('Signature', moduleGroups.signature)}
                {renderAddOptions('More', moduleGroups.more)}
              </div>
            )}
          </div>
          {modules.map((module, index) => {
            const meta = getModuleMeta(module.type);
            const moduleId = module.id || module._localId;
            const isFocused = focusedModuleId === moduleId;
            const hasData = isModuleComplete(module);
            return (
              <div
                key={moduleId}
                className={`lumirae-ds-list-card p-4 text-left ${isFocused ? 'is-active' : ''}`}
                style={{
                  borderColor: isFocused ? 'var(--lumirae-accent-primary)' : 'var(--lumirae-border-default)',
                  boxShadow: isFocused ? '0 16px 36px rgba(33, 27, 29, 0.10)' : undefined,
                }}
              >
                <div className="flex w-full items-start gap-3">
                  <span
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl text-xs font-black"
                    style={{ backgroundColor: 'var(--lumirae-accent-soft)', color: theme.accent }}
                  >
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <span className="min-w-0 flex-1 text-[10px] font-black uppercase leading-snug tracking-widest">
                        {meta.label}
                      </span>
                      <span
                        className="inline-flex shrink-0 items-center gap-1 rounded-full bg-white/75 px-3 py-1 text-[8px] font-black uppercase tracking-widest"
                        style={{ color: hasData ? 'var(--lumirae-state-success)' : 'var(--lumirae-text-secondary)' }}
                      >
                        {hasData ? <FaCheckCircle /> : null}
                        {hasData ? 'Ready' : 'Todo'}
                      </span>
                    </div>
                    <p className="mt-2 text-[11px] font-medium leading-relaxed opacity-55">
                      {meta.role}
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => handleModuleSelect(moduleId)}
                  className="mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-white/75 px-4 py-3 text-[9px] font-black uppercase tracking-widest shadow-sm"
                  style={{ color: theme.accent }}
                >
                  <FaPen /> Edit this module
                </button>

                <div className="mt-3 grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    className="flex min-h-[2.5rem] items-center justify-center gap-2 rounded-full bg-white px-3 text-[8px] font-black uppercase tracking-widest shadow-sm disabled:opacity-30"
                    aria-label={`Move ${meta.label} up`}
                    disabled={index === 0}
                    style={{ color: theme.accent }}
                    onClick={(event) => handleMoveModule(event, moduleId, -1)}
                  >
                    <FaArrowUp /> Up
                  </button>
                  <button
                    type="button"
                    className="flex min-h-[2.5rem] items-center justify-center gap-2 rounded-full bg-white px-3 text-[8px] font-black uppercase tracking-widest shadow-sm disabled:opacity-30"
                    aria-label={`Move ${meta.label} down`}
                    disabled={index === modules.length - 1}
                    style={{ color: theme.accent }}
                    onClick={(event) => handleMoveModule(event, moduleId, 1)}
                  >
                    <FaArrowDown /> Down
                  </button>
                  <button
                    type="button"
                    className="flex min-h-[2.5rem] items-center justify-center gap-2 rounded-full bg-white px-3 text-[8px] font-black uppercase tracking-widest shadow-sm"
                    aria-label={`Delete ${meta.label}`}
                    style={{ color: 'var(--lumirae-state-error)' }}
                    onClick={(event) => handleRemoveModule(event, moduleId, meta.label)}
                  >
                    <FaTrash /> Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : !focusedModuleId ? (
        <div className="mt-4 rounded-[1.5rem] border border-dashed bg-white/60 p-5 text-center">
          <div className="text-[10px] font-black uppercase tracking-widest opacity-55">
            Start with a photo story, letter, voice note, blessing wall, or final card.
          </div>
          <button
            type="button"
            className="lumirae-ds-button lumirae-ds-button-primary mt-4 inline-flex items-center justify-center gap-2 px-4"
            onClick={() => setShowAddPanel((value) => !value)}
          >
            {showAddPanel ? <FaTimes /> : <FaPlus />}
            {showAddPanel ? 'Close Modules' : 'Add Module'}
          </button>
          {showAddPanel && (
            <div className="mt-4 text-left">
              {renderAddOptions('Recommended', moduleGroups.recommended)}
              {renderAddOptions('Signature', moduleGroups.signature)}
              {renderAddOptions('More', moduleGroups.more)}
            </div>
          )}
        </div>
      ) : null}
      </>
      )}
    </section>
  );
};

export default ChapterListStep;
